import { useEffect, useState, useCallback } from "react";
import { Navigate, useParams, Link } from "react-router-dom";
import { Loader2, Send, FileText, Plus, Trash2, Sparkles, RefreshCw, Lock, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiClient, API, formatApiErrorDetail } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { QUOTE_STATUS_LABELS, euros, formatDateTime } from "@/constants/business_status";

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
      <h3 className="text-xs uppercase tracking-widest text-orange font-bold mb-3">{title}</h3>
      {children}
    </div>
  );
}

export default function QuoteEditorPage() {
  const { admin } = useAdminAuth();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [pricingInputs, setPricingInputs] = useState({ distance_km: "", duration_h: "", volume_m3: "", movers_count: "", floors_no_elevator: "" });
  const [showPricing, setShowPricing] = useState(false);
  const [tab, setTab] = useState("editor");

  const load = useCallback(() => {
    apiClient.get(`/admin/quotes/${id}`).then((r) => {
      setData(r.data);
      const inv = r.data.inventory;
      setPricingInputs((p) => ({ ...p, volume_m3: p.volume_m3 || inv?.retained_volume_m3 || "" }));
    }).catch(() => toast.error("Devis introuvable"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (admin === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>;
  if (admin === false) return <Navigate to="/admin/login" replace />;
  if (!data) return <AdminShell><div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy" /></div></AdminShell>;

  const { quote, contact, organization, checklist, versions, locked } = data;

  const patch = async (updates, msg = "Enregistré") => {
    setSaving(true);
    try {
      const r = await apiClient.patch(`/admin/quotes/${id}`, updates);
      setData((d) => ({ ...d, quote: r.data.quote, checklist: r.data.checklist }));
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const setField = (field, value) => setData((d) => ({ ...d, quote: { ...d.quote, [field]: value } }));

  const saveText = () => patch({
    title: quote.title, intro_text: quote.intro_text, understanding_text: quote.understanding_text,
    special_conditions: quote.special_conditions, next_steps_text: quote.next_steps_text,
    valid_until: quote.valid_until,
    scope_included: quote.scope_included, scope_excluded: quote.scope_excluded,
    mission_summary: quote.mission_summary, team_means: quote.team_means,
  });

  const updateLine = (idx, field, value) => {
    const lines = [...(quote.lines || [])];
    lines[idx] = { ...lines[idx], [field]: value };
    setField("lines", lines);
  };
  const saveLines = (lines) => patch({ lines: (lines || quote.lines || []).map((l) => ({
    ...l, qty: parseFloat(l.qty) || 1, unit_price_cents: parseInt(l.unit_price_cents) || 0 })) }, "Lignes enregistrées");
  const addLine = () => setField("lines", [...(quote.lines || []), { label: "", qty: 1, unit: "forfait", unit_price_cents: 0, kind: "service" }]);
  const removeLine = (idx) => { const lines = quote.lines.filter((_, i) => i !== idx); setField("lines", lines); saveLines(lines); };

  const computePricing = async (apply) => {
    try {
      const inputs = {};
      Object.entries(pricingInputs).forEach(([k, v]) => { if (v !== "" && v != null) inputs[k] = parseFloat(v); });
      inputs.vat_rate = quote.totals?.vat_rate ?? 20;
      const r = await apiClient.post(`/admin/quotes/${id}/compute-pricing`, { inputs, apply_lines: apply });
      setData((d) => ({ ...d, quote: r.data.quote }));
      toast.success(apply ? "Tarification appliquée aux lignes" : `Calcul : ${euros(r.data.pricing.ttc_cents)} TTC — marge ${r.data.pricing.margin_rate} %`);
      if (apply) setShowPricing(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runAi = async (kind) => {
    setAiLoading(true);
    try {
      const r = await apiClient.post(`/admin/quotes/${id}/ai/${kind}`, { force: false });
      if (r.data.configured === false) toast.info(r.data.message);
      setAiResult({ kind, ...r.data });
    } catch { toast.error("Analyse indisponible"); } finally { setAiLoading(false); }
  };

  const sendQuote = async () => {
    if (!window.confirm(`Envoyer le devis ${quote.quote_number} à ${contact?.email} ? Une version immuable sera créée.`)) return;
    setSending(true);
    try {
      const r = await apiClient.post(`/admin/quotes/${id}/send`, {});
      toast.success(`Devis envoyé (version ${r.data.version})`);
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(det?.blocking ? `Bloqué : ${det.blocking.join(" • ")}` : formatApiErrorDetail(det));
    } finally { setSending(false); }
  };

  const revise = async () => {
    try { await apiClient.post(`/admin/quotes/${id}/revise`); toast.success("Révision ouverte — une nouvelle version sera créée à l'envoi"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setInsurance = async (remove) => {
    if (remove) return patch({ remove_insurance: true }, "Mention retirée");
    const title = window.prompt("Intitulé de la mention (ex. Garantie dommages)", quote.insurance?.title || "");
    if (!title) return;
    const description = window.prompt("Description (facultatif)", quote.insurance?.description || "") || "";
    patch({ insurance: { title, description, amount_cents: quote.insurance?.amount_cents || null, included: true } }, "Mention ajoutée");
  };

  const ttc = quote.totals?.total_ttc_cents || 0;
  const margin = quote.pricing_snapshot?.result;

  return (
    <AdminShell>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="mr-auto">
          <h1 data-testid="quote-editor-header" className="font-heading font-extrabold text-2xl text-navy">{quote.quote_number}</h1>
          <div className="text-sm text-muted-foreground">
            {contact?.display_name} {organization ? `• ${organization.legal_name}` : ""} {data.case ? <>• <Link className="underline" to={`/admin/dossiers/${data.case.id}`}>{data.case.case_number}</Link></> : ""}
          </div>
        </div>
        <span data-testid="quote-status-badge" className="text-xs font-bold bg-navy/5 text-navy rounded-full px-3 py-1.5">
          {QUOTE_STATUS_LABELS[quote.status]} {quote.current_version ? `• V${quote.current_version}` : ""}
        </span>
        <a href={`${API}/admin/quotes/${id}/preview.pdf`} target="_blank" rel="noreferrer">
          <Button data-testid="quote-preview-pdf" variant="outline" className="rounded-xl"><FileText className="w-4 h-4 mr-1" /> Aperçu PDF</Button>
        </a>
        {locked ? (
          <Button data-testid="quote-revise" onClick={revise} variant="outline" className="rounded-xl"><RefreshCw className="w-4 h-4 mr-1" /> Réviser (V{(quote.current_version || 0) + 1})</Button>
        ) : (
          <Button data-testid="quote-send" onClick={sendQuote} disabled={sending || !checklist.can_send}
            className="bg-orange hover:bg-orange/90 text-white rounded-xl font-semibold">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Envoyer
          </Button>
        )}
      </div>

      {locked && (
        <div data-testid="quote-locked-banner" className="flex items-center gap-2 bg-navy/5 rounded-xl px-4 py-2.5 mb-4 text-sm text-navy">
          <Lock className="w-4 h-4" /> Version envoyée immuable. Cliquez sur « Réviser » pour préparer une nouvelle version.
        </div>
      )}

      <div className="flex gap-2 mb-4 lg:hidden">
        {[["context", "Dossier"], ["editor", "Devis"], ["assist", "Contrôles"]].map(([k, l]) => (
          <button key={k} data-testid={`quote-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold ${tab === k ? "bg-navy text-white" : "bg-white text-navy"}`}>{l}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_300px] gap-4 items-start">
        {/* GAUCHE : contexte dossier */}
        <div className={`${tab !== "context" ? "hidden lg:block" : ""}`}>
          <Section title="Client">
            <div className="text-sm font-semibold text-navy">{contact?.display_name}</div>
            <div className="text-xs text-muted-foreground">{contact?.phone}<br />{contact?.email || "Pas d'e-mail"}</div>
            {organization && <div className="text-xs mt-1">{organization.legal_name}</div>}
          </Section>
          {data.case && (
            <Section title="Dossier">
              <div className="text-xs space-y-1">
                <div><span className="text-muted-foreground">Prestation :</span> {data.case.service_type || "—"}</div>
                <div><span className="text-muted-foreground">Date :</span> {data.case.requested_date || "—"}</div>
                <div><span className="text-muted-foreground">Départ :</span> {data.case.origin_address?.city || "—"}</div>
                <div><span className="text-muted-foreground">Arrivée :</span> {data.case.destination_address?.city || "—"}</div>
                {data.case.customer_message && <div className="pt-1 text-muted-foreground line-clamp-4">« {data.case.customer_message} »</div>}
              </div>
              <Link to={`/admin/dossiers/${data.case.id}/inventaire`} className="text-xs text-orange font-semibold underline block mt-2">Inventaire &amp; accès →</Link>
            </Section>
          )}
          <Section title="Inventaire">
            <div className="text-sm">{data.inventory?.retained_volume_m3 ? `${data.inventory.retained_volume_m3} m³ retenus` : "Aucun inventaire"}</div>
            <div className="text-xs text-muted-foreground">{(data.inventory?.items || []).length} objet(s)</div>
          </Section>
        </div>

        {/* CENTRE : éditeur */}
        <div className={`${tab !== "editor" ? "hidden lg:block" : ""}`}>
          <Section title="Contenu du devis">
            <label className="text-xs font-semibold text-navy">Titre</label>
            <Input data-testid="quote-title" value={quote.title || ""} onChange={(e) => setField("title", e.target.value)} className="mb-2 bg-white" disabled={locked} />
            <label className="text-xs font-semibold text-navy">Introduction personnalisée</label>
            <Textarea data-testid="quote-intro" value={quote.intro_text || ""} onChange={(e) => setField("intro_text", e.target.value)} rows={3} className="mb-2 bg-white" disabled={locked} />
            <label className="text-xs font-semibold text-navy">Compréhension du besoin</label>
            <Textarea data-testid="quote-understanding" value={quote.understanding_text || ""} onChange={(e) => setField("understanding_text", e.target.value)} rows={3} className="mb-2 bg-white" disabled={locked} />
            <label className="text-xs font-semibold text-navy">Conditions particulières</label>
            <Textarea data-testid="quote-conditions" value={quote.special_conditions || ""} onChange={(e) => setField("special_conditions", e.target.value)} rows={2} className="mb-2 bg-white" disabled={locked} />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="text-xs font-semibold text-navy">Validité (AAAA-MM-JJ)</label>
                <Input data-testid="quote-valid-until" value={quote.valid_until || ""} onChange={(e) => setField("valid_until", e.target.value)} className="bg-white" disabled={locked} />
              </div>
            </div>
            {!locked && <Button data-testid="quote-save-texts" onClick={saveText} disabled={saving} size="sm" className="bg-navy text-white rounded-lg">Enregistrer les textes</Button>}
          </Section>

          <Section title="Proposition financière">
            {!locked && (
              <div className="flex gap-2 mb-3">
                <Button data-testid="quote-open-pricing" onClick={() => setShowPricing(!showPricing)} size="sm" variant="outline" className="rounded-lg">
                  <Calculator className="w-4 h-4 mr-1" /> Moteur tarifaire
                </Button>
                <Button data-testid="quote-add-line" onClick={addLine} size="sm" variant="outline" className="rounded-lg"><Plus className="w-4 h-4 mr-1" /> Ligne</Button>
              </div>
            )}
            {showPricing && !locked && (
              <div data-testid="pricing-panel" className="bg-graylight rounded-xl p-3 mb-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                {[["distance_km", "Distance (km)"], ["duration_h", "Durée (h)"], ["volume_m3", "Volume (m³)"],
                  ["movers_count", "Manutentionnaires"], ["floors_no_elevator", "Étages sans asc."]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-[11px] font-semibold text-navy">{l}</label>
                    <Input data-testid={`pricing-${k}`} type="number" value={pricingInputs[k]} onChange={(e) => setPricingInputs((p) => ({ ...p, [k]: e.target.value }))} className="h-8 bg-white" />
                  </div>
                ))}
                <div className="col-span-2 md:col-span-3 flex gap-2 pt-1">
                  <Button data-testid="pricing-compute" onClick={() => computePricing(false)} size="sm" variant="outline" className="rounded-lg">Calculer</Button>
                  <Button data-testid="pricing-apply" onClick={() => computePricing(true)} size="sm" className="bg-navy text-white rounded-lg">Calculer et remplir les lignes</Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {(quote.lines || []).map((l, idx) => (
                <div key={l.id || idx} className="flex gap-2 items-center" data-testid={`quote-line-${idx}`}>
                  <Input value={l.label} placeholder="Désignation" onChange={(e) => updateLine(idx, "label", e.target.value)} className="flex-1 h-9 bg-white" disabled={locked} />
                  <Input type="number" value={l.qty} onChange={(e) => updateLine(idx, "qty", e.target.value)} className="w-16 h-9 bg-white" disabled={locked} title="Quantité" />
                  <Input type="number" value={l.unit_price_cents} onChange={(e) => updateLine(idx, "unit_price_cents", e.target.value)} className="w-24 h-9 bg-white" disabled={locked} title="PU (centimes)" />
                  <span className="text-xs w-20 text-right font-semibold">{euros((parseFloat(l.qty) || 0) * (parseInt(l.unit_price_cents) || 0))}</span>
                  {!locked && <button data-testid={`quote-line-remove-${idx}`} onClick={() => removeLine(idx)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
              {(quote.lines || []).length === 0 && <div className="text-sm text-muted-foreground" data-testid="quote-no-lines">Aucune ligne — utilisez le moteur tarifaire ou ajoutez une ligne.</div>}
            </div>
            {!locked && (quote.lines || []).length > 0 && (
              <Button data-testid="quote-save-lines" onClick={() => saveLines()} disabled={saving} size="sm" className="bg-navy text-white rounded-lg mt-3">Enregistrer les lignes</Button>
            )}
            <div className="mt-4 border-t pt-3 text-sm space-y-1 max-w-xs ml-auto">
              <div className="flex justify-between"><span className="text-muted-foreground">Total HT</span><span className="font-semibold">{euros(quote.totals?.total_ht_cents)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">TVA ({quote.totals?.vat_rate} %)</span><span>{euros(quote.totals?.vat_cents)}</span></div>
              <div className="flex justify-between text-base font-extrabold text-navy"><span>Total TTC</span><span data-testid="quote-total-ttc">{euros(ttc)}</span></div>
            </div>
          </Section>

          <Section title="Assurance (facultative — jamais automatique)">
            {quote.insurance ? (
              <div className="flex items-center justify-between" data-testid="quote-insurance-block">
                <div><div className="text-sm font-semibold">{quote.insurance.title}</div>
                  <div className="text-xs text-muted-foreground">{quote.insurance.description}</div></div>
                {!locked && <Button data-testid="quote-insurance-remove" onClick={() => setInsurance(true)} size="sm" variant="outline" className="rounded-lg text-red-600">Retirer</Button>}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground" data-testid="quote-no-insurance">Aucune mention d'assurance ne figurera sur le devis.</span>
                {!locked && <Button data-testid="quote-insurance-add" onClick={() => setInsurance(false)} size="sm" variant="outline" className="rounded-lg">Ajouter une mention</Button>}
              </div>
            )}
          </Section>
        </div>

        {/* DROITE : assistant + contrôles */}
        <div className={`${tab !== "assist" ? "hidden lg:block" : ""}`}>
          <Section title="Checklist avant envoi">
            {checklist.blocking.length === 0 ? (
              <div className="text-sm text-green-700 font-semibold" data-testid="checklist-ok">✓ Prêt à envoyer</div>
            ) : checklist.blocking.map((b, i) => (
              <div key={i} className="text-xs text-red-600 mb-1" data-testid={`checklist-blocking-${i}`}>● {b}</div>
            ))}
            {checklist.warnings.map((w, i) => (
              <div key={i} className="text-xs text-amber-600 mt-1">▲ {w}</div>
            ))}
          </Section>
          {margin && (
            <Section title="Marge (interne)">
              <div className="text-sm">Coût interne : <strong>{euros(margin.internal_cost_cents)}</strong></div>
              <div className="text-sm">Marge : <strong className={margin.margin_rate < 15 ? "text-red-600" : "text-green-700"}>{euros(margin.margin_cents)} ({margin.margin_rate} %)</strong></div>
            </Section>
          )}
          <Section title="Assistant IA">
            {!data.ai_configured ? (
              <div className="text-xs text-muted-foreground" data-testid="ai-disabled">Assistant non configuré — les fonctions métier restent disponibles.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {[["summarize_need", "Résumer le besoin"], ["propose_description", "Proposer un texte"],
                  ["missing_info", "Infos manquantes"], ["detect_inconsistencies", "Incohérences"],
                  ["propose_means", "Moyens conseillés"]].map(([k, l]) => (
                  <Button key={k} data-testid={`ai-${k}`} onClick={() => runAi(k)} disabled={aiLoading} size="sm" variant="outline" className="rounded-lg text-xs h-8">
                    <Sparkles className="w-3 h-3 mr-1" />{l}
                  </Button>
                ))}
              </div>
            )}
            {aiLoading && <Loader2 className="w-4 h-4 animate-spin text-navy mt-2" />}
            {aiResult?.result && (
              <div data-testid="ai-result" className="mt-3 text-xs bg-graylight rounded-xl p-3 whitespace-pre-wrap max-h-64 overflow-y-auto">
                {aiResult.result}
                {aiResult.kind === "propose_description" && !locked && (
                  <Button size="sm" variant="outline" className="rounded-lg mt-2 w-full" data-testid="ai-apply-description"
                    onClick={() => { setField("understanding_text", aiResult.result); toast.info("Texte inséré — pensez à enregistrer"); }}>
                    Utiliser comme « Compréhension du besoin »
                  </Button>
                )}
              </div>
            )}
          </Section>
          <Section title={`Versions (${versions.length})`}>
            {versions.length === 0 ? <div className="text-xs text-muted-foreground">Aucune version envoyée</div> :
              versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0" data-testid={`version-${v.version}`}>
                  <span className="font-semibold">V{v.version} — {QUOTE_STATUS_LABELS[v.status] || v.status}</span>
                  <span className="text-muted-foreground">{formatDateTime(v.sent_at || v.created_at)}</span>
                  <a href={`${API}/admin/quote-versions/${v.id}/pdf`} target="_blank" rel="noreferrer" className="text-orange font-semibold underline">PDF</a>
                </div>
              ))}
          </Section>
          <FollowupsSection quoteId={id} />
        </div>
      </div>
    </AdminShell>
  );
}
